import { collection, doc, getDocs, onSnapshot, query, where, runTransaction, writeBatch, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { makeTask } from "../tasks/makeTask";
import { parseKey } from "../../shared/dates";
import { KEY_DATES_COLLECTION, repeatRollover } from "./keyDates";

const keyDatesRef = (uid) => collection(db, "users", uid, KEY_DATES_COLLECTION);
const keyDateRef = (uid, id) => doc(db, "users", uid, KEY_DATES_COLLECTION, id);
const taskRef = (uid, id) => doc(db, "users", uid, "tasks", id);

// Live list of the user's Key Dates. Returns the unsubscribe function for effect cleanup.
export const subscribeKeyDates = (uid, onKeyDates, onError) =>
  onSnapshot(query(keyDatesRef(uid)), (snap) => onKeyDates(snap.docs.map(d => ({ id: d.id, ...d.data() }))), onError);

// What's written with the user's own fields: `yearly` to match `repeat`, so a tab still running an older version reads a
// yearly date the same way, and `day`, the day of the month the user chose (dayOf), whenever they set the date. Moving
// a date on (rollKeyDateOn) leaves `day` alone.
const withYearly = (fields) => ({
  ...fields,
  ...("repeat" in fields && { yearly: fields.repeat === "yearly" }),
  ...(fields.date && { day: parseKey(fields.date).getDate() }),
});

// A new Key Date and the tasks typed with it, in one batch, so a date is never saved without them. When the
// date is for a goal, its first tasks go in that goal too (the editor says so).
export const addKeyDate = (uid, { title, date, remindBefore, repeat = null, goalId = null }, taskTexts = []) => {
  const batch = writeBatch(db);
  const ref = doc(keyDatesRef(uid));
  batch.set(ref, { ...withYearly({ title, date, remindBefore, repeat }), goalId, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  taskTexts.forEach(text => {
    batch.set(doc(collection(db, "users", uid, "tasks")), {
      ...makeTask({ text, keyDateId: ref.id, parentGoalId: goalId }), createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
  });
  return batch.commit().then(() => ref.id);
};

// `update`, not a merge: a date deleted on another device stays deleted.
export const updateKeyDate = (uid, id, fields) => {
  const batch = writeBatch(db);
  batch.update(keyDateRef(uid, id), { ...withYearly(fields), updatedAt: serverTimestamp() });
  return batch.commit();
};

// Deleting a Key Date keeps its tasks: they stay in the task list, no longer tied to a date.
export const deleteKeyDate = (uid, id, taskIds = []) => {
  const batch = writeBatch(db);
  batch.delete(keyDateRef(uid, id));
  taskIds.forEach(taskId => batch.update(taskRef(uid, taskId), { keyDateId: null, updatedAt: serverTimestamp() }));
  return batch.commit();
};

// Moves a repeating Key Date whose day has passed on to its next one, with its tasks (see repeatRollover). The
// tasks are found on the server, not from the app's list, which may not have loaded yet. A transaction: two
// devices opening the app the same morning must not move it twice, so the date is read fresh and nothing is
// written if another device has already moved it. Resolves to the new date, or null.
export const rollKeyDateOn = async (uid, keyDateId) => {
  const taskDocs = await getDocs(query(collection(db, "users", uid, "tasks"), where("keyDateId", "==", keyDateId)));
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(keyDateRef(uid, keyDateId));
    if (!snap.exists()) return null;
    const taskSnaps = await Promise.all(taskDocs.docs.map(d => tx.get(taskRef(uid, d.id))));
    const tasks = taskSnaps.filter(t => t.exists()).map(t => ({ id: t.id, ...t.data() }));
    const roll = repeatRollover({ id: keyDateId, ...snap.data() }, tasks);
    if (!roll) return null;
    tx.update(keyDateRef(uid, keyDateId), { date: roll.date, updatedAt: serverTimestamp() });
    roll.taskChanges.forEach(({ id, fields }) => tx.update(taskRef(uid, id), { ...fields, updatedAt: serverTimestamp() }));
    return roll.date;
  });
};

// Deleting a goal takes it off every Key Date that was for it, in the goal's delete batch. Needs a connection.
export const unlinkGoalFromKeyDates = async (batch, uid, goalId) => {
  const snap = await getDocs(query(keyDatesRef(uid), where("goalId", "==", goalId)));
  snap.docs.forEach(d => batch.update(keyDateRef(uid, d.id), { goalId: null, updatedAt: serverTimestamp() }));
};
